
import { BrgEvent, CalendarDay } from '../types';
import { getTextColor } from '../../../lib/colorUtils';

interface CalendarGridProps {
  days: CalendarDay[];
  onEventClick: (event: BrgEvent) => void;
  onDayClick: (day: CalendarDay) => void;
}

/**
 * Calendar Grid Component
 * Displays a 5-column calendar grid with events
 * Shows up to 3 events per day, with "+more" indicator for additional events
 */
export default function CalendarGrid({
  days,
  onEventClick,
  onDayClick,
}: CalendarGridProps) {
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  // Filter to show only Monday-Friday (5 columns)
  // Calculate offset to start on Monday
  const offset = days.length > 0 ? 0 : 0;
  const displayDays = days.slice(0, offset + 35); // 5 weeks

  return (
    <div style={{ borderRadius: '0.375rem', overflow: 'hidden', border: '1px solid #d1d5db' }}>
      {/* Header with Day Names */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        backgroundColor: '#111827',
        gap: 0
      }}>
        {dayNames.map(day => (
          <div
            key={day}
            style={{
              padding: '12px 16px',
              textAlign: 'center',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Body */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 0
      }}>
        {displayDays.map((day, idx) => (
          <div
            key={idx}
            style={{
              minHeight: '112px',
              borderRight: idx % 5 === 4 ? 'none' : '1px solid #d1d5db',
              borderBottom: idx >= displayDays.length - 5 ? 'none' : '1px solid #d1d5db',
              padding: '8px',
              cursor: 'pointer',
              backgroundColor: !day.isCurrentMonth ? '#f3f4f6' : 'white',
              transition: 'background-color 0.2s'
            }}
            onClick={() => day.isCurrentMonth && day.events.length > 0 && onDayClick(day)}
            onMouseEnter={(e) => {
              if (day.isCurrentMonth) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = !day.isCurrentMonth ? '#f3f4f6' : 'white';
            }}
          >
            {/* Day number */}
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: day.isCurrentMonth ? '#111827' : '#9ca3af'
            }}>
              {day.date}
            </div>

            {/* Events (up to 3) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {day.events.slice(0, 3).map(event => (
                <div
                  key={event.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                  }}
                  style={{
                    padding: '4px 8px',
                    color: getTextColor(event.brgColor),
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '4px',
                    backgroundColor: event.brgColor,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 0.2s'
                  }}
                  title={event.eventName}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  {event.eventName}
                </div>
              ))}

              {/* +more indicator */}
              {day.events.length > 3 && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onDayClick(day);
                  }}
                  style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#2563eb',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  +{day.events.length - 3} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
